// Profile page configuration constants

// Product Type options (multi-select)
export const PRODUCT_TYPES = [
  { id: "therapeutic_asset", label: "Therapeutic asset / pipeline", description: "Developing drug candidates or therapeutic products" },
  { id: "computational_ai", label: "Computational / AI platform", description: "AI/ML-driven discovery or analysis platform" },
  { id: "enabling_technology", label: "Enabling technology", description: "Technologies that enable drug discovery/development" },
  { id: "research_tool", label: "Research tool / assay / model", description: "Tools, assays, or disease models for research" },
  { id: "delivery_technology", label: "Delivery technology", description: "Drug delivery systems or formulation technology" },
  { id: "manufacturing", label: "Manufacturing / process technology", description: "Manufacturing, CDMO, or process development" },
  { id: "other_product", label: "Other", description: "Other product type" }
];

// Business Model options (multi-select with primary)
export const BUSINESS_MODELS = [
  { id: "internal_asset_dev", label: "Internal asset development", description: "Developing proprietary pipeline internally" },
  { id: "platform_partnerships", label: "Platform partnerships", description: "Partnering platform capabilities with pharma/biotech" },
  { id: "fee_for_service", label: "Fee-for-service", description: "Providing services for a fee" },
  { id: "software_saas", label: "Software / SaaS", description: "Software or SaaS business model" },
  { id: "licensing_technology", label: "Licensing technology", description: "Licensing technology or IP to others" },
  { id: "sale_of_tools", label: "Sale of tools / kits / reagents", description: "Selling research tools, kits, or reagents" },
  { id: "data_licensing", label: "Data licensing", description: "Licensing data or datasets" },
  { id: "other_model", label: "Other", description: "Other business model" }
];

// Derive company archetype based on product types and business model
export const deriveCompanyArchetype = (productTypes = [], businessModels = [], primaryBusinessModel = "") => {
  const hasTherapeuticAsset = productTypes.includes("therapeutic_asset");
  const hasComputationalAI = productTypes.includes("computational_ai");
  const hasEnablingTech = productTypes.includes("enabling_technology");
  const hasResearchTool = productTypes.includes("research_tool");
  const hasDeliveryTech = productTypes.includes("delivery_technology");
  const hasManufacturing = productTypes.includes("manufacturing");
  
  const hasInternalAssetDev = businessModels.includes("internal_asset_dev") || primaryBusinessModel === "internal_asset_dev";
  const hasPlatformPartnerships = businessModels.includes("platform_partnerships");
  const hasFeeForService = businessModels.includes("fee_for_service");
  const hasSoftwareSaas = businessModels.includes("software_saas");
  
  // 1. Asset-centric biotech
  // Therapeutic asset selected + primary model = internal asset development
  if (hasTherapeuticAsset && primaryBusinessModel === "internal_asset_dev" && !hasComputationalAI) {
    return {
      archetype: "asset_centric_biotech",
      label: "Asset-centric Biotech",
      description: "Traditional biotech focused on developing proprietary therapeutic assets"
    };
  }
  
  // 2. TechBio platform with internal pipeline
  // Therapeutic asset + computational platform + platform partnerships/internal asset development
  if (hasTherapeuticAsset && hasComputationalAI && (hasInternalAssetDev || hasPlatformPartnerships)) {
    return {
      archetype: "techbio_platform",
      label: "TechBio Platform",
      description: "AI/computational platform with internal therapeutic pipeline"
    };
  }
  
  // 3. Platform company
  // Discovery/computational platform without own lead asset focus
  // Platform + fee-for-service / PaaS
  if (hasComputationalAI && !hasTherapeuticAsset && (hasFeeForService || hasPlatformPartnerships || hasSoftwareSaas)) {
    return {
      archetype: "platform_company",
      label: "Platform Company",
      description: "Computational/discovery platform providing services or partnerships"
    };
  }
  
  // 4. Enabling technology company
  // Enabling technology / tool / delivery / manufacturing tech
  if (hasEnablingTech || hasResearchTool || hasDeliveryTech || hasManufacturing) {
    return {
      archetype: "enabling_tech_company",
      label: "Enabling Technology Company",
      description: "Company providing enabling technologies, tools, or services"
    };
  }
  
  // 5. Software/Data company (if primarily software/SaaS or data licensing)
  if (hasSoftwareSaas || businessModels.includes("data_licensing")) {
    return {
      archetype: "software_data_company",
      label: "Software / Data Company",
      description: "Software, SaaS, or data-focused life sciences company"
    };
  }
  
  // Default fallback
  if (hasTherapeuticAsset) {
    return {
      archetype: "asset_centric_biotech",
      label: "Asset-centric Biotech",
      description: "Biotech developing therapeutic assets"
    };
  }
  
  return {
    archetype: "other",
    label: "Life Sciences Company",
    description: "Life sciences company"
  };
};

// Legacy company types for backwards compatibility
export const COMPANY_TYPES = [
  { id: "platform_service", label: "Platform as a service (comp.)", description: "You provide computational/analysis services to others", archetype: "Platform as a service (comp.)" },
  { id: "asset_generation", label: "Asset generation (wet)", description: "You develop internal therapeutic assets", archetype: "Asset generation (wet)" },
  { id: "internal_platform_asset", label: "Internal platform + asset generation", description: "You have an internal platform and develop your own assets", archetype: "Internal platform + asset generation" },
  { id: "internal_external_platform_asset", label: "Internal and external platform + asset generation", description: "You provide platform services externally and develop your own assets", archetype: "Internal and external platform + asset generation" },
  { id: "enabling_technologies", label: "Enabling technologies", description: "You develop enabling technology for others", archetype: "Enabling technologies" }
];

// Get archetype label from selected type (single selection - legacy)
export const getArchetypeLabel = (selectedType) => {
  const type = COMPANY_TYPES.find(t => t.id === selectedType);
  return type?.archetype || null;
};

// Fundraising timeframe options
export const FUNDRAISING_TIMEFRAMES = [
  { value: "0-3", label: "0-3 months (urgent)" },
  { value: "3-6", label: "3-6 months" },
  { value: "6-12", label: "6-12 months" },
  { value: "12+", label: "12+ months (relationship building)" }
];

export const TARGET_FOCUS_OPTIONS = [
  { value: "known_validated", label: "Known validated target" },
  { value: "novel_undisclosed", label: "Novel/undisclosed target" },
  { value: "multi_target", label: "Multi-target approach" }
];

export const RUNWAY_OPTIONS = [
  { value: "0-6", label: "0-6 months" },
  { value: "6-12", label: "6-12 months" },
  { value: "12-18", label: "12-18 months" },
  { value: "18+", label: "18+ months" }
];

export const COMPANY_LOCATIONS = [
  { value: "uk", label: "United Kingdom" },
  { value: "europe", label: "Europe (non-UK)" },
  { value: "us", label: "United States" },
  { value: "asia", label: "Asia Pacific" },
  { value: "other", label: "Other" }
];

export const FUNDING_STAGES = [
  { value: "pre-seed", label: "Pre-Seed" },
  { value: "seed", label: "Seed" },
  { value: "series-a", label: "Series A" },
  { value: "series-b", label: "Series B" },
  { value: "series-c", label: "Series C+" },
  { value: "public", label: "Public" }
];

export const DEVELOPMENT_STAGES = [
  { value: "discovery", label: "Discovery" },
  { value: "preclinical", label: "Pre-clinical" },
  { value: "phase1", label: "Phase 1" },
  { value: "phase2", label: "Phase 2" },
  { value: "phase3", label: "Phase 3" },
  { value: "approved", label: "Approved" }
];

export const INDICATIONS = [
  "Oncology", "Rare Diseases", "Neurology", "Immunology", "Cardiology",
  "Infectious Diseases", "Metabolic Disorders", "Ophthalmology", 
  "Dermatology", "Respiratory", "Gastroenterology", "Hematology", "Agnostic"
];

export const MODALITIES = [
  "Small Molecule",
  "Antibody-Based (incl. ADCs)",
  "Other Biologics",
  "Cell Therapy",
  "Gene Therapy",
  "RNA Therapeutics",
  "Microbiome",
  "Platform / Enabling Technology"
];
